import type { SeoResolveData } from '@/features/seo-listing/types';
import {
  getListingChromeKind,
  type ListingChromeKind,
} from '@/features/seo-listing/utils/seo-listing';

export type ResolveListingChromeKindInput = {
  pathname: string;
  pageType?: string | null;
  selectedFandomsCount?: number;
  selectedCharactersCount?: number;
};

export type ResolveListingFandomTitleInput = {
  kind: ListingChromeKind;
  pageName?: string | null;
  pageType?: string | null;
  fandomTitle?: string | null;
};

/** SEO pageName is a fandom label only for fandom (or characters-hub) resolve. */
const isSeoPageNameFandomMeta = (pageType: string | null | undefined): boolean =>
  pageType === 'fandom' || pageType === 'fandom_characters';

/**
 * Resolve the fandom label used in H1 / breadcrumbs.
 *
 * Catalog `/fanfics` resolve often has pageName "Фанфики". On legacy GET
 * `?fandoms=` with disabled permalink SEO that catalog seoPage is still passed
 * as initialSeoPage — it must not override the filter-provided fandom title
 * (otherwise reload shows «Фэндом «Фанфики»» and crumbs Главная / Фанфики / Фанфики).
 */
export const resolveListingFandomTitle = ({
  kind,
  pageName,
  pageType,
  fandomTitle,
}: ResolveListingFandomTitleInput): string => {
  const fromFilter = fandomTitle?.trim() || '';
  const fromPage = pageName?.trim() || '';
  const pageNameIsFandom = isSeoPageNameFandomMeta(pageType);

  if (kind === 'catalog') {
    return fromFilter;
  }

  if (kind === 'fandom' || kind === 'fandom_characters') {
    return (pageNameIsFandom ? fromPage : '') || fromFilter;
  }

  // Character / pairing / tag: filter fandom title first; never treat catalog
  // listing pageName ("Фанфики") as the fandom crumb.
  return fromFilter || (pageNameIsFandom ? fromPage : '');
};

/**
 * Infer chrome kind from path / SEO pageType / selected filters.
 * Active filters win over a stale SEO permanentUrl: e.g. Naruto character
 * select can leave seoPage.permanentUrl on `/fanfics/fandoms/naruto-*` while
 * `characters=` is set — path-first logic then flashed character crumbs and
 * reverted to fandom-only after resolve.
 */
export const resolveListingChromeKind = ({
  pathname,
  pageType,
  selectedFandomsCount = 0,
  selectedCharactersCount = 0,
}: ResolveListingChromeKindInput): ListingChromeKind => {
  // Filter identity is authoritative when it is at least as specific as path.
  if (selectedFandomsCount === 1 && selectedCharactersCount === 1) {
    return 'fandom_character';
  }
  if (selectedFandomsCount === 1 && selectedCharactersCount > 1) {
    return 'fandom';
  }

  const kindFromPath = getListingChromeKind(pathname);
  if (kindFromPath !== 'catalog') return kindFromPath;
  // Empty catalog address bar must win over a stale resolve pageType left after
  // soft SEO pushState + browser Back (/fanfics still holding fandom seoPage).
  if (selectedFandomsCount === 0 && selectedCharactersCount === 0) {
    return 'catalog';
  }
  if (pageType === 'fandom') return 'fandom';
  if (pageType === 'character' || pageType === 'fandom_character') return 'fandom_character';
  if (pageType === 'pairing' || pageType === 'fandom_pairing') return 'fandom_pairing';
  if (pageType === 'tag') return 'tag';
  if (selectedFandomsCount === 1) return 'fandom';
  return kindFromPath;
};

export const readFandomIdFromSeoPage = (seoPage: SeoResolveData | null): string | undefined => {
  if (!seoPage) return undefined;
  const listingMatch = seoPage.listingGetUrl?.match(/[?&]fandoms=([^&]+)/);
  if (listingMatch?.[1]) return decodeURIComponent(listingMatch[1]);
  const pageKeyMatch = seoPage.pageKey?.match(/:fandom:(\d+)/);
  return pageKeyMatch?.[1];
};

/** SEO h1 is only safe when resolve pageType matches the inferred chrome kind. */
export const isSeoH1CompatibleWithChromeKind = (
  kind: ListingChromeKind,
  pageType: string | null | undefined,
): boolean => {
  if (!pageType) return kind === 'catalog';
  switch (kind) {
    case 'catalog':
      return pageType === 'listing';
    case 'fandom':
      return pageType === 'fandom';
    case 'fandom_character':
      return pageType === 'character' || pageType === 'fandom_character';
    case 'fandom_pairing':
      return pageType === 'pairing' || pageType === 'fandom_pairing';
    case 'fandom_characters':
      return pageType === 'fandom_characters';
    case 'tag':
      return pageType === 'tag';
    default:
      return false;
  }
};
